import React, { useEffect, useRef, useState } from "react";
import Slider, { createSliderWithTooltip } from "rc-slider";
import "rc-slider/assets/index.css";
import moment from "moment";
import {
  getCurrentTimeIsFromPip,
  getPltvPaused,
  setCurrentTimeIsFromPip,
  setPltvPaused,
  setTimeUpdateIntervalId
} from "../../../statics/config";
import { playSafely } from "../playSafely";
import { isSafari } from "react-device-detect";

const SliderWithTooltip = createSliderWithTooltip(Slider);

const PlayerBar = ({
  channel,
  program,
  player,
  isTimeshiftEnable,
  setIsLiveEdge,
  isPltv,
  isProgramPlayback,
  restartTv,
  rewindBackTv,
  isPaused
}) => {
  const [progress, setProgress] = useState(0);
  const [time, setTime] = useState(null);
  const liveTracker = useRef(null);
  const programBeginTime = useRef(0);
  const programEndTime = useRef(0);
  const currentProgramProgress = useRef(0);
  const timeDiff = useRef(0);
  const progressRef = useRef(0);
  const sliderRef = useRef(null);
  const afterProgress = useRef(null);
  const isPltvRef = useRef(null);
  const restartTvRef = useRef(null);
  const rewindBackTvRef = useRef(null);
  const isLoadedPltv = useRef(false);
  const timeUpdateIntervalId = useRef(null);
  const isPlayTriggered = useRef(null);
  const seekTimers = useRef(new Set());
  const seekOnPlay = useRef(null);
  const scheduleSeek = (callback, delay) => {
    const timer = setTimeout(() => {
      seekTimers.current.delete(timer);
      if (!player?.isDisposed?.()) callback();
    }, delay);
    seekTimers.current.add(timer);
  };
  let currentTimeIsFromPip = getCurrentTimeIsFromPip();
  let now;
  let currentTime;
  let progress_;

  const clamp = (val, min = 0, max = 1000) =>
    Math.max(min, Math.min(max, val));

  const getSeekableBounds = () => {
    const seekable = player?.seekable && player.seekable();
    if (!seekable || seekable.length <= 0) {
      return null;
    }

    return {
      start: seekable.start(0),
      end: seekable.end(seekable.length - 1)
    };
  };

  const getLiveCurrentEpoch = () => {
    const tracker =
      liveTracker.current ||
      (player?.getChild && player.getChild("LiveTracker"));

    if (!tracker || typeof tracker.liveCurrentTime !== "function") {
      return null;
    }

    const value = tracker.liveCurrentTime();
    return Number.isFinite(value) ? value : null;
  };

  const toEpochSeconds = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) {
      // Values greater than 1e12 are milliseconds.
      return value > 1_000_000_000_000 ? value / 1000 : value;
    }

    if (typeof value === "string" && value.trim()) {
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) {
        return numeric > 1_000_000_000_000 ? numeric / 1000 : numeric;
      }

      const strictMoment = moment(
        value,
        [
          "YYYY.MM.DD HH:mm",
          "YYYY.MM.DD HH:mm:ss",
          "YYYY-MM-DD HH:mm:ss",
          "YYYY-MM-DDTHH:mm:ss",
          "YYYY-MM-DDTHH:mm:ss.SSSZ"
        ],
        true
      );
      if (strictMoment.isValid()) {
        return strictMoment.valueOf() / 1000;
      }

      const parsed = Date.parse(value);
      if (!Number.isNaN(parsed)) {
        return parsed / 1000;
      }
    }

    return 0;
  };

  const calcProgress = (val = 0) => {
    now = new Date().getTime() / 1000 - timeDiff.current / 1000; //seconds
    currentTime = player.currentTime(); // it is seconds but different format

    const programDuration = programEndTime.current - programBeginTime.current;
    if (programDuration <= 0) {
      return;
    }

    let playbackOffsetSec = currentTime;
    let liveEdgeOffsetSec = Math.max(
      0,
      Math.min(programDuration, now - programBeginTime.current)
    );

    const seekableBounds = getSeekableBounds();
    const isLiveStream = player.duration() === Infinity;
    if (seekableBounds) {
      const seekableStart = seekableBounds.start;
      const seekableEnd = seekableBounds.end;
      playbackOffsetSec = Math.max(0, currentTime - seekableStart);
      if (isLiveStream) {
        liveEdgeOffsetSec = Math.max(0, seekableEnd - seekableStart);
      }
    }

    const liveCurrentEpoch = getLiveCurrentEpoch();
    if (liveCurrentEpoch && liveCurrentEpoch > 1_000_000) {
      const derivedPlaybackEpoch = now - (liveCurrentEpoch - currentTime);
      if (Number.isFinite(derivedPlaybackEpoch)) {
        playbackOffsetSec = derivedPlaybackEpoch - programBeginTime.current;
      }
    }

    const normalizedPlaybackOffset = Math.max(
      0,
      Math.min(programDuration, playbackOffsetSec)
    );

    const playbackEpoch = programBeginTime.current + normalizedPlaybackOffset;
    progress_ = ((normalizedPlaybackOffset - val) / programDuration) * 1000;

    const liveEdgeProgress = (liveEdgeOffsetSec / programDuration) * 1000;
    currentProgramProgress.current = clamp(liveEdgeProgress);

    if (val === 0) {
      setTime(moment(playbackEpoch * 1000).format("HH:mm:ss"));
      const normalizedProgress = clamp(progress_);
      setProgress(normalizedProgress);
      progressRef.current = normalizedProgress;
    } else {
      return progress_;
    }
  };

  const calcCurrentProgramProgress = () => {
    let now_ = new Date().getTime() / 1000 - timeDiff.current / 1000;
    let duration_ = programEndTime.current - programBeginTime.current;
    if (duration_ <= 0) {
      return;
    }

    let progress_ = ((now_ - programBeginTime.current) / duration_) * 1000;
    currentProgramProgress.current = clamp(progress_);
    listenTimeUpdate();
  };

  const seekToSelectedProgress = (value, paused = false) => {
    const selectedProgress = clamp(value);
    if (selectedProgress <= currentProgramProgress.current || paused) {
      let programDuration = programEndTime.current - programBeginTime.current;
      if (programDuration <= 0) {
        return;
      }

      let seekTime =
        ((progressRef.current - selectedProgress) * programDuration) / 1000;

      myLogger("programDuration", programDuration);
      myLogger("seekTime", seekTime);

      // For a live stream (duration = Infinity), player.currentTime() only covers
      // the small live buffer. We need to switch to DVR/PLTV mode first.
      const isLiveStream = player.duration() === Infinity;
      const targetTime = player.currentTime() - seekTime;

      if (isLiveStream && targetTime < 0) {
        // Switch to PLTV/DVR mode; after stream loads, seek to this progress position
        afterProgress.current = selectedProgress;
        setIsLiveEdge("1");
        setProgress(selectedProgress);
        progressRef.current = selectedProgress;
      } else if (isSafari) {
        if (isPlayTriggered.current) {
          player.currentTime(targetTime);
        } else {
          const onPlay = () => {
            scheduleSeek(() => {
              player.currentTime(targetTime);
            }, 200);
            isPlayTriggered.current = true;
            player.off("play", onPlay);
            seekOnPlay.current = null;
          };
          seekOnPlay.current = onPlay;
          player.on("play", onPlay);
        }
        setProgress(selectedProgress);
        progressRef.current = selectedProgress;
      } else {
        scheduleSeek(() => {
          myLogger("player.currenttime", player.currentTime());
          player.currentTime(targetTime);
        }, 200);
        setProgress(selectedProgress);
        progressRef.current = selectedProgress;
      }
    } else {
      if (isPltv) {
        // Do not auto-switch to live on transient progress jitter.
        // Live return should happen only via Back Live button.
        const cappedProgress = clamp(currentProgramProgress.current);
        setProgress(cappedProgress);
        progressRef.current = cappedProgress;
      } else {
        setProgress(progressRef.current);
      }
    }
    listenTimeUpdate();
  };

  const listenTimeUpdate = () => {
    timeUpdateIntervalId.current && offTimeUpdate();
    timeUpdateIntervalId.current = setInterval(() => {
      // console.log("timeUpdateIntervalId:",timeUpdateIntervalId.current);
      calcProgress();
    }, 1000);

    setTimeUpdateIntervalId(timeUpdateIntervalId.current);
  };

  const offTimeUpdate = () => {
    clearInterval(timeUpdateIntervalId.current);
    setTimeUpdateIntervalId(null);
  };

  useEffect(() => {
    if (player) {
      programBeginTime.current = toEpochSeconds(
        program?.startTime ??
          program?.starttime ??
          program?.begintime ??
          channel?.startTime ??
          channel?.starttime ??
          channel?.begintime
      );
      programEndTime.current = toEpochSeconds(
        program?.endTime ?? program?.endtime ?? channel?.endTime ?? channel?.endtime
      );

      if (programBeginTime.current <= 0 || programEndTime.current <= 0) {
        programBeginTime.current = toEpochSeconds(
          channel?.startTime ?? channel?.starttime ?? channel?.begintime
        );
        programEndTime.current = toEpochSeconds(
          channel?.endTime ?? channel?.endtime
        );
      }

      restartTvRef.current = 0;
      isLoadedPltv.current = false;
      isPltvRef.current = false;
      rewindBackTvRef.current = false;
      afterProgress.current = null;
      const onLoadedMetadata = () => {
        isPlayTriggered.current = false;
        offTimeUpdate();
        liveTracker.current = player.getChild("LiveTracker");

        if (restartTvRef.current) {
          restartTvRef.current = 0;
          isLoadedPltv.current = true;
          seekToSelectedProgress(0);
        } else if (
          (isPltvRef.current || afterProgress.current !== null) &&
          !rewindBackTvRef.current &&
          !getPltvPaused()
        ) {
          isLoadedPltv.current = true;
          const targetProgress = afterProgress.current;
          afterProgress.current = null;
          if (targetProgress !== null && targetProgress !== undefined) {
            // Stream loaded from program begin. Seek to exact position.
            const programDuration = programEndTime.current - programBeginTime.current;
            const targetTimeSec = (targetProgress / 1000) * programDuration;
            scheduleSeek(() => {
              player.currentTime(targetTimeSec);
            }, isSafari ? 300 : 100);
            setProgress(targetProgress);
            progressRef.current = targetProgress;
            listenTimeUpdate();
          } else {
            seekToSelectedProgress(0);
          }
        } else {
          restartTvRef.current = 0;
          isLoadedPltv.current = rewindBackTvRef.current;
          if (isProgramPlayback) {
            const seekableBounds = getSeekableBounds();
            const seekStart = seekableBounds ? seekableBounds.start : 0;
            if (seekableBounds) {
              scheduleSeek(() => {
                player.currentTime(seekStart);
                calcProgress();
              }, isSafari ? 300 : 100);
            } else {
              scheduleSeek(() => {
                player.currentTime(0);
                calcProgress();
              }, isSafari ? 300 : 100);
            }
          }
          calcCurrentProgramProgress();
          calcProgress();
          rewindBackTvRef.current = false;
          if (getPltvPaused()) {
            isLoadedPltv.current = true;
            player.pause();
            setPltvPaused(false);
          }
        }
      };
      player.on("loadedmetadata", onLoadedMetadata);
      return () => {
        player.off("loadedmetadata", onLoadedMetadata);
        seekTimers.current.forEach(clearTimeout);
        seekTimers.current.clear();
      };
    }
  }, [program, channel, player]);

  useEffect(() => {
    isPltvRef.current = isPltv;
  }, [isPltv]);

  useEffect(() => {
    if (currentTimeIsFromPip > 0 && player) {
      player.pause();
      setIsLiveEdge("1");
      //afterProgress.current = currentTimeIsFromPip;
      scheduleSeek(() => {
        player.ready(() => {
          console.log("ready");
          isLoadedPltv.current = true;
          //seekToSelectedProgress(currentTimeIsFromPip);
          player.currentTime(currentTimeIsFromPip);
          setCurrentTimeIsFromPip(null);
          playSafely(player);
        });
      }, 1000);
    }
  }, [currentTimeIsFromPip]);

  useEffect(() => {
    rewindBackTvRef.current = rewindBackTv;
  }, [rewindBackTv]);

  useEffect(() => {
    offTimeUpdate();
    restartTvRef.current = restartTv;
    if (isLoadedPltv.current || restartTv > 0) {
      seekToSelectedProgress(0);
      restartTvRef.current = 0;
      console.log("restart bar", restartTv);
    }
  }, [restartTv]);

  useEffect(() => {
    return () => {
      seekTimers.current.forEach(clearTimeout);
      seekTimers.current.clear();
      if (seekOnPlay.current && player) player.off("play", seekOnPlay.current);
      timeUpdateIntervalId.current && offTimeUpdate();
    };
  }, [player]);

  const myLogger = (key, val = "") => {
    console.log(`playerBar ${key}: `, val);
  };

  return (
    <div className="rc-slider-wrapper" ref={sliderRef} id="rc-slider">
      <SliderWithTooltip
        min={0}
        max={1000}
        step={1}
        value={progress}
        onChange={(value) => {
          setProgress(value);
        }}
        onAfterChange={(val) => {
          afterProgress.current = val;
          seekToSelectedProgress(val);
        }}
        onBeforeChange={() => {
          offTimeUpdate();
        }}
        tipFormatter={() => time}
        disabled={!isTimeshiftEnable}
      />
    </div>
  );
};

export default PlayerBar;
